import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Box, Typography, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import api from "../api/api"; // your axios instance
import { CircularProgress } from "@mui/material";

const DeletedUsers = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [spinner, setSpinner] = useState(false);


  // Fetch deleted users
  const getDeletedUsers = async () => {
      setSpinner(true); // show spinner while fetching

    try {
      await api
        .get("/admin/deleted-users")
        .then((response) => {
          const usersArray = Array.isArray(response.data)
            ? response.data
            : [response.data]; // wrap single object into array
          setRows(usersArray);
          setSpinner(false)
        })
        .catch((error) => {
          console.error("Error fetching deleted users:", error);
          setSpinner(false);
        });
    } catch (error) {
      console.error(error);
      setSpinner(false);
    }
  };

  // Restore user with SweetAlert confirmation
  const restoreUser = async (uid, userName) => {
    const result = await Swal.fire({
      title: t("confirmRestore"),
      text: `${t("confirmRestoreText")} "${userName}"?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "var(--sf-primary)",
      cancelButtonColor: "#64748b",
      confirmButtonText: t("restore"),
      cancelButtonText: t("cancel"),
      customClass: {
        popup: 'swal-minimal',
        confirmButton: 'swal-confirm-btn',
        cancelButton: 'swal-cancel-btn'
      }
    });

    if (result.isConfirmed) {
      try {
        await api
          .post("/admin/restore-user", { user_uid: uid })
          .then((response) => {
            Swal.fire({
              title: t("success"),
              text: t("userRestored"),
              icon: "success",
              timer: 2000,
              showConfirmButton: false,
              customClass: {
                popup: 'swal-minimal'
              }
            });
            getDeletedUsers(); // refresh list after restore
          })
          .catch((error) => {
            Swal.fire({
              title: t("error"),
              text: t("errorRestoringUser"),
              icon: "error",
              confirmButtonColor: "var(--sf-primary)",
              customClass: {
                popup: 'swal-minimal'
              }
            });
            console.error("Error restoring user:", error);
          });
      } catch (error) {
        Swal.fire({
          title: t("error"),
          text: t("errorRestoringUser"),
          icon: "error",
          confirmButtonColor: "var(--sf-primary)",
          customClass: {
            popup: 'swal-minimal'
          }
        });
        console.error(error);
      }
    }
  };

  // Format date helper function
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return "-";
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return "-";
    }
  };

  useEffect(() => {
    getDeletedUsers();
  }, []);

  // Filter rows by search input
  const filteredRows = rows.filter(
    (row) =>
      row.name?.toLowerCase().includes(searchText.toLowerCase()) ||
      row.email?.toLowerCase().includes(searchText.toLowerCase()) ||
      row.phone_number?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    { 
      field: "name", 
      headerName: t("name"), 
      flex: 1 
    },
    { 
      field: "email", 
      headerName: t("email"), 
      flex: 1.5 
    },
    {
      field: "phone_number",
      headerName: t("phone"),
      flex: 1,
      renderCell: (params) => {
        const phone = params?.row?.phone_number;
        return phone && phone.trim() !== "" ? phone : "-";
      },
    },
    { 
      field: "role", 
      headerName: t("role"), 
      flex: 1 
    },
    {
      field: "created_at",
      headerName: t("createdAt"),
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        return <span>{formatDate(params?.row?.created_at)}</span>;
      },
    },
    {
      field: "deleted_at",
      headerName: t("deletedAt"),
      flex: 1,
      renderCell: (params) => {
        return <span>{formatDate(params?.row?.deleted_at)}</span>;
      },
    },
    {
      field: "actions",
      headerName: t("actions"),
      sortable: false,
      width: 150,
      renderCell: (params) => {
        if (!params?.row) return null; // safeguard
        return (
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: "var(--sf-night-primary, var(--sf-primary))",
              color: "white",
              textTransform: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 500,
              padding: "6px 16px",
              minWidth: "80px",
              boxShadow: "none",
              "&:hover": { 
                backgroundColor: "var(--sf-night-primary, var(--sf-primary-dark))",
                boxShadow: "0 2px 8px var(--sf-night-focus-ring, rgba(38, 166, 183, 0.3))",
              },
              "&:active": {
                backgroundColor: "var(--sf-night-primary, var(--sf-primary-dark))",
              },
            }}
            onClick={() => restoreUser(params.row.uid, params.row.name)}
          >
            {t("restore")}
          </Button>
        );
      },
    },
  ];

  return (
    <>
      {/* Custom SweetAlert2 styles */}
      <style>
        {`
          .swal-minimal {
            border-radius: 12px !important;
            box-shadow: 0 10px 25px var(--sf-night-shadow, rgba(0, 0, 0, 0.1)) !important;
          }
          .swal-confirm-btn {
            border-radius: 8px !important;
            font-weight: 500 !important;
            padding: 8px 20px !important;
            font-size: 14px !important;
          }
          .swal-cancel-btn {
            border-radius: 8px !important;
            font-weight: 500 !important;
            padding: 8px 20px !important;
            font-size: 14px !important;
          }
        `}
      </style>
      
      <Box 
        sx={{ 
          maxWidth: "1200px",
          mx: "auto",
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h4"
            sx={{ 
              color: "var(--sf-heading)", 
              fontWeight: 600,
              mb: 3,
              fontSize: { xs: "1.75rem", md: "2.125rem" }
            }}
          >
            {t("deletedUsers")}
          </Typography>
          
          {/* Search bar */}
          <TextField
            variant="outlined"
            placeholder={t("searchPlaceholder")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{
              maxWidth: "400px",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "var(--sf-night-surface, white)",
                borderRadius: "8px",
                fontSize: "14px",
                transition: "all 0.2s ease",
                "&:hover": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--sf-primary)",
                  },
                },
                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "var(--sf-primary)",
                    borderWidth: "2px",
                  },
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--sf-night-border, #e5e7eb)",
                },
              },
              "& .MuiInputBase-input": {
                padding: "10px 14px",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "var(--sf-night-muted, #9ca3af)",
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Data Grid Container */}
        <Box
          sx={{
            backgroundColor: "var(--sf-night-surface, white)",
            borderRadius: "12px",
            border: "1px solid var(--sf-night-border, #f3f4f6)",
            overflow: "hidden",
            boxShadow: "0 1px 3px var(--sf-night-shadow, rgba(0, 0, 0, 0.06))",
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "var(--sf-night-surface, #f9fafb)",
              borderBottom: "1px solid var(--sf-night-border, #e5e7eb)",
              minHeight: "48px",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              color: "var(--sf-heading)",
              fontWeight: 600,
              fontSize: "13px",
            },
            "& .MuiDataGrid-row": {
              borderBottom: "1px solid var(--sf-night-border, #f3f4f6)",
              transition: "background-color 0.15s ease",
              "&:hover": {
                backgroundColor: "var(--sf-night-surface, #f8fafc)",
              },
              "&:last-child": {
                borderBottom: "none",
              },
            },
            "& .MuiDataGrid-cell": {
              color: "var(--sf-night-text, #374151)",
              fontSize: "14px",
              padding: "12px 16px",
              borderRight: "none",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid var(--sf-night-border, #e5e7eb)",
              backgroundColor: "var(--sf-night-surface, #f9fafb)",
              minHeight: "48px",
            },
            "& .MuiTablePagination-root": {
              color: "var(--sf-night-text-secondary, #6b7280)",
              fontSize: "14px",
            },
            "& .MuiTablePagination-selectIcon": {
              color: "var(--sf-night-brand, var(--sf-primary))",
            },
            "& .MuiDataGrid-selectedRowCount": {
              display: "none",
            },
            "& .MuiDataGrid-menuIcon": {
              color: "var(--sf-night-muted, #9ca3af)",
            },
            "& .MuiDataGrid-sortIcon": {
              color: "var(--sf-night-brand, var(--sf-primary))",
            },
          }}
        > {spinner ? (   <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "200px",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.uid}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[5, 10, 25]}
            disableSelectionOnClick
            disableRowSelectionOnClick
            autoHeight
            density="comfortable"
          /> )}
        </Box>
      </Box>
    </>
  );
};

export default DeletedUsers;
